import {Alert, Button, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {IconPlus} from '@tabler/icons-react'
import {useMemo, useState} from 'react'

import {isStockEquipmentType} from 'src/equipment/stock-equipment'

import {useRentalAssetTypes} from '../assets/rental-assets/use-rental-asset-types'
import {useRentalAssets} from '../assets/rental-assets/use-rental-assets'
import {useWorkerTypes} from '../assets/worker-types/use-worker-types'
import {useWorkers} from '../assets/workers/use-workers'
import {useInvalidateDispatchAttentionCount} from '../dispatch/use-dispatch'
import {useInvalidateProjectDeployedEquipment} from '../project-deployed-equipment/use-project-deployed-equipment'
import {
    isScheduleLockedServiceJob,
    SCHEDULE_LOCKED_JOB_HINT,
} from '../service-schedules/preview-labels'
import {AddRequirementModal} from './add-requirement-modal'
import {
    assignmentsToInputs,
    fulfillmentGroupsFromJobDetail,
    removeAssignment,
} from './assignment-grouping-utils'
import {useEditRights} from './edit-rights-context'
import {FulfillmentGroupCard} from './fulfillment-group'
import type {JobDataLayer} from './job-data-layer'
import {parseApiDate} from './job-schedule-format'
import {ManagePoolModal} from './manage-pool-modal'
import {getAvailableSlots} from './requirement-utils'
import {useApplyStockEquipmentAssignmentDelta} from './stock-equipment/use-stock-equipment'
import type {JobDetail} from './types'

type RequirementsAssignmentsSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

export function RequirementsAssignmentsSection({
    job,
    jobDataLayer,
    onJobUpdated,
}: RequirementsAssignmentsSectionProps) {
    const {canEdit, canDispatch} = useEditRights()
    const {data: allWorkers = []} = useWorkers({includeArchived: true})
    const {data: rentalAssetTypes = []} = useRentalAssetTypes()
    const {data: workerTypes = []} = useWorkerTypes()
    const {data: rentalAssets = []} = useRentalAssets()
    const workers = useMemo(
        () => allWorkers.filter((worker) => worker.archivedAt == null),
        [allWorkers],
    )
    const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null)
    const [addRequirementOpened, setAddRequirementOpened] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const applyStockDelta = useApplyStockEquipmentAssignmentDelta(job.id)
    const invalidateDispatchAttentionCount = useInvalidateDispatchAttentionCount()
    const invalidateProjectDeployedEquipment = useInvalidateProjectDeployedEquipment()
    const scheduleLocked = isScheduleLockedServiceJob(job.scheduleRole)

    const groups = useMemo(() => fulfillmentGroupsFromJobDetail(job, allWorkers), [job, allWorkers])
    const firstPartyRentalAssetIds = useMemo(
        () => new Set(rentalAssets.map((asset) => asset.id)),
        [rentalAssets],
    )
    const rentalAssetsById = useMemo(
        () => new Map(rentalAssets.map((asset) => [asset.id, asset])),
        [rentalAssets],
    )
    const stockTypeIds = useMemo(
        () =>
            new Set(
                rentalAssetTypes
                    .filter((type) => isStockEquipmentType(type.isUniquelyIdentified))
                    .map((type) => type.id),
            ),
        [rentalAssetTypes],
    )
    const activeGroup = groups.find((group) => group.key === activeGroupKey) ?? null

    const currentRequirements = job.requirements.map((requirement) => ({
        requirementType: requirement.requirementType,
        quantity: requirement.quantity,
        workerTypeId: requirement.workerTypeId ?? null,
        rentalAssetTypeId: requirement.rentalAssetTypeId,
        billingRate: requirement.billingRate,
        billingType: requirement.billingType,
    }))
    const {canAddRequirement} = getAvailableSlots(
        currentRequirements,
        rentalAssetTypes,
        workerTypes,
    )

    const jobStartDate = useMemo(() => parseApiDate(job.startDate), [job.startDate])

    async function handleRemove(resourceId: string) {
        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.saveAssignments(
                job.id,
                removeAssignment(assignmentsToInputs(job.assignments), resourceId),
            )
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to remove assignment')
        } finally {
            setSaving(false)
        }
    }

    async function handleRemoveStockGroup(
        rentalAssetTypeId: string,
        assignedCount: number,
        offeringId?: string,
    ) {
        if (assignedCount < 1) {
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            const {job: updatedJob} = await applyStockDelta.mutateAsync({
                rentalAssetTypeId,
                delta: -assignedCount,
                thirdPartyRentalAssetOfferingId: offeringId,
            })
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to remove assignments')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Stack gap={4}>
                    <Title order={3}>Requirements & Assignments</Title>
                    {canEdit ? (
                        <Text size="sm" c="dimmed">
                            Click a requirement category to manage its pool and quantity.
                        </Text>
                    ) : null}
                    {scheduleLocked && (
                        <Text size="sm" c="dimmed">
                            {SCHEDULE_LOCKED_JOB_HINT}
                        </Text>
                    )}
                </Stack>

                {errorMessage && (
                    <Alert color="red" title="Could not save assignment">
                        {errorMessage}
                    </Alert>
                )}

                {groups.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No requirements yet. Add one to start assigning resources.
                    </Text>
                ) : (
                    <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                        {groups.map((group) => (
                            <FulfillmentGroupCard
                                key={group.key}
                                group={group}
                                jobStartDate={jobStartDate}
                                isStockType={
                                    (group.stockEquipmentGroups?.length ?? 0) > 0 ||
                                    (group.rentalAssetTypeId != null &&
                                        stockTypeIds.has(group.rentalAssetTypeId))
                                }
                                firstPartyRentalAssetIds={firstPartyRentalAssetIds}
                                rentalAssetsById={rentalAssetsById}
                                onOpenPool={
                                    canEdit ? () => setActiveGroupKey(group.key) : undefined
                                }
                                onRemove={
                                    canEdit && canDispatch
                                        ? (resourceId) => void handleRemove(resourceId)
                                        : undefined
                                }
                                onRemoveStockGroup={
                                    canEdit && canDispatch
                                        ? (rentalAssetTypeId, assignedCount, offeringId) =>
                                              void handleRemoveStockGroup(
                                                  rentalAssetTypeId,
                                                  assignedCount,
                                                  offeringId,
                                              )
                                        : undefined
                                }
                            />
                        ))}
                    </SimpleGrid>
                )}

                {canEdit ? (
                    <Button
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setAddRequirementOpened(true)}
                        disabled={!canAddRequirement || saving || scheduleLocked}
                    >
                        Add requirement
                    </Button>
                ) : null}

                <ManagePoolModal
                    opened={activeGroup != null}
                    group={activeGroup}
                    job={job}
                    workers={workers}
                    rentalAssets={rentalAssets}
                    isStockType={
                        (activeGroup?.stockEquipmentGroups?.length ?? 0) > 0 ||
                        (activeGroup?.rentalAssetTypeId != null &&
                            stockTypeIds.has(activeGroup.rentalAssetTypeId))
                    }
                    jobDataLayer={jobDataLayer}
                    onClose={() => setActiveGroupKey(null)}
                    onJobUpdated={onJobUpdated}
                />

                <AddRequirementModal
                    opened={addRequirementOpened}
                    job={job}
                    jobDataLayer={jobDataLayer}
                    onClose={() => setAddRequirementOpened(false)}
                    onJobUpdated={onJobUpdated}
                    onRequirementAdded={(groupKey) => setActiveGroupKey(groupKey)}
                />
            </Stack>
        </Paper>
    )
}
